import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { Card } from '../ui';

interface SubInvoicesTabProps {
  bookingId: number;
  // Main invoice currency: new extras are locked to it so the trip total stays exact
  // (backend enforces the same). Older extras keep their own currency when edited.
  mainCurrency?: string;
  onChanged?: () => void;
}

interface LineItem {
  id?: number;
  description: string;
  qty: number;
  rate: number;
  amount: number;
}

interface SubInvoice {
  id?: number;
  currency: string;
  invoice_label: string;
  total_amount: number;
  discount: number;
  paid: number;
  balance: number;
  status: string;
  items: LineItem[];
}

const CURRENCY_OPTIONS = ["IDR", "USD", "INR", "AUD"];

const SubInvoicesTab: React.FC<SubInvoicesTabProps> = ({ bookingId, mainCurrency, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [subInvoices, setSubInvoices] = useState<SubInvoice[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [currency, setCurrency] = useState('IDR');
  const [label, setLabel] = useState('Additional Services');
  const [discount, setDiscount] = useState(0);
  const [items, setItems] = useState<LineItem[]>([{ description: '', qty: 1, rate: 0, amount: 0 }]);

  useEffect(() => { loadData(); }, [bookingId]);

  const getToken = () => sessionStorage.getItem('jwt_token');

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/invoice-revamp/${bookingId}/sub-invoices`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) setSubInvoices(data.data);
    } catch (e) { console.error('Error:', e); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setCurrency(mainCurrency || 'IDR');
    setLabel('Additional Services');
    setDiscount(0);
    setItems([{ description: '', qty: 1, rate: 0, amount: 0 }]);
    setEditingId(null);
    setShowForm(false);
  };

  const updateItem = (index: number, field: keyof LineItem, value: any) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'qty' || field === 'rate') {
      updated[index].amount = updated[index].qty * updated[index].rate;
    }
    setItems(updated);
  };

  const subtotal = items.reduce((s, i) => s + i.amount, 0);
  const total = subtotal - discount;

  const saveSubInvoice = async () => {
    const validItems = items.filter(i => i.description.trim() && i.amount > 0);
    if (validItems.length === 0) { alert('Add at least one item with description and amount'); return; }
    setSaving(true);
    try {
      const res = await fetch(`/api/invoice-revamp/${bookingId}/sub-invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ id: editingId, currency, invoice_label: label, items, discount }),
      });
      const data = await res.json();
      if (data.success) { resetForm(); loadData(); onChanged?.(); }
      else { alert('Error: ' + (data.detail || data.message)); }
    } catch (e) { alert('Error saving'); }
    finally { setSaving(false); }
  };

  const editSubInvoice = (inv: SubInvoice) => {
    setCurrency(inv.currency);
    setLabel(inv.invoice_label);
    setDiscount(inv.discount);
    setItems(inv.items.length > 0 ? inv.items : [{ description: '', qty: 1, rate: 0, amount: 0 }]);
    setEditingId(inv.id || null);
    setShowForm(true);
  };

  const deleteSubInvoice = async (id: number) => {
    if (!window.confirm('Delete this sub-invoice?')) return;
    try {
      const res = await fetch(`/api/invoice-revamp/${bookingId}/sub-invoices/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) { loadData(); onChanged?.(); }
      else { alert('Error: ' + (data.detail || data.message)); }
    } catch (e) { alert('Error deleting'); }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base sm:text-lg font-semibold">Additional Invoices</h3>
          <p className="text-xs text-gray-500">
            For on-site extras{mainCurrency ? ` — in the main invoice currency (${mainCurrency})` : ''}
          </p>
        </div>
        {!showForm && (
          <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} className="text-xs sm:text-sm">
            + New Sub-Invoice
          </Button>
        )}
      </div>

      {/* Existing sub-invoices */}
      {subInvoices.map((inv) => (
        <Card key={inv.id} className={`mb-3 p-4 ${inv.balance > 0 ? 'border-l-4 border-l-orange-400' : 'border-l-4 border-l-green-400'}`}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm">{inv.invoice_label}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{inv.currency}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  inv.status === 'Completed' ? 'bg-green-100 text-green-700' :
                  inv.status === 'Partial' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>{inv.status}</span>
              </div>
              <div className="text-xs text-gray-400">
                {inv.items.map(i => i.description).join(' • ')}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => editSubInvoice(inv)} className="text-xs text-blue-600 hover:text-blue-800 underline">Edit</button>
              <button onClick={() => deleteSubInvoice(inv.id!)} className="text-xs text-red-500 hover:text-red-700 underline">Delete</button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded p-2 text-center">
              <div className="text-[10px] text-gray-500 uppercase">Total</div>
              <div className="font-bold text-sm">{inv.currency} {inv.total_amount.toLocaleString()}</div>
            </div>
            <div className="bg-gray-50 rounded p-2 text-center">
              <div className="text-[10px] text-gray-500 uppercase">Paid</div>
              <div className="font-bold text-sm text-green-700">{inv.currency} {inv.paid.toLocaleString()}</div>
            </div>
            <div className={`rounded p-2 text-center ${inv.balance > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
              <div className="text-[10px] text-gray-500 uppercase">Balance</div>
              <div className={`font-bold text-sm ${inv.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {inv.currency} {inv.balance.toLocaleString()}
              </div>
            </div>
          </div>
        </Card>
      ))}

      {subInvoices.length === 0 && !showForm && (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <div className="text-3xl mb-2">📦</div>
          <p className="text-gray-400 text-sm mb-3">No additional invoices yet</p>
          <Button size="sm" variant="secondary" onClick={() => { resetForm(); setShowForm(true); }} className="text-xs">
            Create Sub-Invoice
          </Button>
        </div>
      )}

      {/* Add/Edit form */}
      {showForm && (
        <Card className="p-4 border-2 border-blue-200 bg-blue-50/30">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-sm">{editingId ? '✏️ Edit' : '➕ New'} Sub-Invoice</h4>
            <button onClick={resetForm} className="text-xs text-gray-500 hover:text-gray-700">✕ Cancel</button>
          </div>

          {/* Header fields */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Label / Description</label>
              <Input
                value={label}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
                placeholder="e.g. Tour Extension, Airport Fast Track, On-site Activities"
                className="text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
              {mainCurrency ? (
                // Locked: new extras use the main currency; an older extra keeps the currency it was created in
                <div className="p-2 bg-gray-100 rounded text-sm font-medium" title="Additional invoices use the main invoice currency">
                  {currency}
                </div>
              ) : (
                <Select value={currency} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCurrency(e.target.value)} className="text-sm">
                  {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              )}
            </div>
          </div>

          {/* Line items */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700">Line Items</label>
              <button
                onClick={() => setItems([...items, { description: '', qty: 1, rate: 0, amount: 0 }])}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add Item
              </button>
            </div>

            {/* Column headers */}
            <div className="hidden sm:grid sm:grid-cols-24 gap-2 mb-1 px-1">
              <div className="col-span-12 text-[10px] font-medium text-gray-500 uppercase">Description</div>
              <div className="col-span-3 text-[10px] font-medium text-gray-500 uppercase">Qty</div>
              <div className="col-span-4 text-[10px] font-medium text-gray-500 uppercase">Rate ({currency})</div>
              <div className="col-span-4 text-[10px] font-medium text-gray-500 uppercase text-right">Amount</div>
              <div className="col-span-1"></div>
            </div>

            <div className="space-y-2">
              {items.map((item, i) => (
                <div key={i} className="bg-white rounded-lg border p-3">
                  {/* Mobile: stacked */}
                  <div className="sm:hidden space-y-2">
                    <Input
                      value={item.description}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(i, 'description', e.target.value)}
                      placeholder="Description (e.g. Nusa Penida Day Tour)"
                      className="text-sm"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] text-gray-500">Qty</label>
                        <Input type="number" value={item.qty} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(i, 'qty', parseInt(e.target.value) || 0)} className="text-sm" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500">Rate</label>
                        <Input type="number" value={item.rate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(i, 'rate', parseFloat(e.target.value) || 0)} className="text-sm" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500">Amount</label>
                        <div className="p-2 bg-gray-100 rounded text-sm font-medium text-right">{item.amount.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>

                  {/* Desktop: inline */}
                  <div className="hidden sm:flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        value={item.description}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(i, 'description', e.target.value)}
                        placeholder="Description (e.g. Nusa Penida Day Tour)"
                        className="text-sm"
                      />
                    </div>
                    <div className="w-16">
                      <Input type="number" value={item.qty} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(i, 'qty', parseInt(e.target.value) || 0)} className="text-sm text-center" />
                    </div>
                    <div className="w-28">
                      <Input type="number" value={item.rate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(i, 'rate', parseFloat(e.target.value) || 0)} className="text-sm" />
                    </div>
                    <div className="w-28 text-right">
                      <div className="p-2 bg-gray-100 rounded text-sm font-medium">{currency} {item.amount.toLocaleString()}</div>
                    </div>
                    <button
                      onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Discount + Totals */}
          <div className="flex justify-end mb-4">
            <div className="w-72 space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Subtotal:</span>
                <span>{currency} {subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Discount:</span>
                <div className="w-28">
                  <Input
                    type="number"
                    value={discount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDiscount(parseFloat(e.target.value) || 0)}
                    className="text-sm text-right"
                  />
                </div>
              </div>
              <div className="flex justify-between font-bold border-t pt-2">
                <span>Total:</span>
                <span>{currency} {total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={saveSubInvoice} disabled={saving} size="sm" className="text-xs sm:text-sm">
              {saving ? 'Saving...' : editingId ? '💾 Update Sub-Invoice' : '💾 Create Sub-Invoice'}
            </Button>
            <Button variant="secondary" onClick={resetForm} size="sm" className="text-xs sm:text-sm">Cancel</Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SubInvoicesTab;
